import React from 'react';
import { FadeIn } from './ui/FadeIn';
export function FuturePacingSection() {
  return <section className="py-12" data-id="element-6">
      <div className="max-w-2xl mx-auto editorial-text" data-id="element-7">
        <FadeIn data-id="element-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-9">
            What Your Dentist Knows About Biofilm (But Hasn't Mentioned)
          </h2>
          <div className="rounded-xl overflow-hidden mb-6" data-id="element-10">
            <img src="https://cdn.magicpatterns.com/uploads/gC9uRpQ2X6jDYaMFrxhJsn/hf_20260403_050930_0117f8a9-5e1a-49e8-bcc2-d4eb1a6d3026_(2).png" alt="Research paper showing oral biofilms are a primary cause of oral diseases" className="w-full h-auto object-cover" data-id="element-11" />
          </div>
          <p data-id="element-12">
            Unfortunately, many people don't know biofilm is considered the
            primary root cause of the most common dental problems - even gum
            disease.
          </p>
          <p className="font-[400]" data-id="element-13">
            As a matter of fact, it's one of the most extensively studied topics
            in modern dental research.
          </p>
          <p data-id="element-14">Which means your dentist is almost certainly aware of it.</p>
          <p data-id="element-15">
            So why has nobody ever mentioned it to you? I'll get to that in a
            moment…
          </p>
          <p className="font-[900]" data-id="element-16">
            First, the most important thing you should know is this:
          </p>
          <p data-id="element-17">
            In the vast majority of cases, problems like this aren't just
            inevitable — they're actually avoidable.
          </p>
          <p data-id="element-18">
            And in almost every case — the solution has nothing to do with
            brushing harder, flossing more, or sitting in that chair again.
          </p>
          <p data-id="element-19">
            In fact, once my patients understand how to simply address it
            themselves at home - something shifts.
          </p>
          <p className="font-[700]" data-id="element-20">
            Within just a couple of months they're not the same person who
            initially walked into my office…
          </p>

          <div className="bg-teal-50 rounded-xl p-6 my-8 border border-teal-100" data-id="element-21">
            <ul className="space-y-4 font-sans text-gray-800" data-id="element-22">
              <li className="flex items-start" data-id="element-23">
                <svg className="w-6 h-6 text-teal-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-25" />
                </svg>
                <span data-id="element-26">Their gums have stopped bleeding.</span>
              </li>
              <li className="flex items-start" data-id="element-27">
                <svg className="w-6 h-6 text-teal-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-28">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-29" />
                </svg>
                <span data-id="element-30">
                  Their breath is fresh — not just for twenty minutes after
                  brushing, but all day.
                </span>
              </li>
              <li className="flex items-start" data-id="element-31">
                <svg className="w-6 h-6 text-teal-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-32">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-33" />
                </svg>
                <span data-id="element-34">
                  Their teeth feel smooth and clean in a way they haven't since
                  they were young.
                </span>
              </li>
              <li className="flex items-start" data-id="element-35">
                <svg className="w-6 h-6 text-teal-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-36">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-37" />
                </svg>
                <span data-id="element-38">
                  That slick, polished feeling you run your tongue across and
                  can't stop noticing.
                </span>
              </li>
              <li className="flex items-start" data-id="element-39">
                <svg className="w-6 h-6 text-teal-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-40">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-41" />
                </svg>
                <span data-id="element-42">
                  No more sensitivity. No more yellow. No more dreading that
                  next appointment.
                </span>
              </li>
            </ul>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-43">
          <p data-id="element-44">
            But honestly — after 25 years of fixing teeth, those are the results
            I expect.
          </p>
          <p className="font-[700]" data-id="element-45">
            What I never get used to seeing is what happens to the person…
          </p>
          <p data-id="element-46">
            They begin smiling in photos - genuinely, with teeth - without
            staging the angle to hide anything.
          </p>
          <p data-id="element-47">
            They speak and laugh freely in conversation, without having to cover
            their mouth.
          </p>
          <p data-id="element-48">
            Not only do they feel younger, but even their family notices —
            without them saying a word about what they changed.
          </p>
          <p data-id="element-49">But more importantly… they feel proud of themselves…</p>
          <p data-id="element-50">Not for having perfect teeth - but for not giving up.</p>
          <p className="font-[700]" data-id="element-51">And here's what may surprise you…</p>
          <p data-id="element-52">None of them got there with a $1,500 deep cleaning…</p>
          <p className="font-semibold text-gray-900" data-id="element-53">
            It's something they do at home. In under a minute a day. For a
            fraction of the cost.
          </p>
          <p data-id="element-54">
            But before I tell you what it is — you need to understand what
            causes a biofilm crisis to occur in the first place.
          </p>
        </FadeIn>
      </div>
    </section>;
}